import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { X, Zap, Target, Flame, Crown, Shuffle, CheckCircle, Lock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

const difficulties = [
  {
    id: "easy",
    name: "Basic",
    icon: <Zap className="w-6 h-6" />,
    color: "from-green-400 to-emerald-500",
    bgColor: "bg-green-50",
    borderColor: "border-green-300",
    textColor: "text-green-700",
    description: "Fundamental concepts, tax slabs, basic sections",
    recommended: "New to Income Tax",
    gp: "8 GP / question",
    gatedBy: null,
  },
  {
    id: "medium",
    name: "Moderate",
    icon: <Target className="w-6 h-6" />,
    color: "from-amber-400 to-orange-500",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-300",
    textColor: "text-amber-700",
    description: "Comparisons, calculations, multiple sections",
    recommended: "Some tax knowledge",
    gp: "10 GP / question",
    gatedBy: null,
  },
  {
    id: "hard",
    name: "Advanced",
    icon: <Flame className="w-6 h-6" />,
    color: "from-rose-400 to-red-500",
    bgColor: "bg-rose-50",
    borderColor: "border-rose-300",
    textColor: "text-rose-700",
    description: "Edge cases, Old vs New Act specifics, complex scenarios",
    recommended: "CA students & professionals",
    gp: "15 GP / question",
    gatedBy: null,
  },
  {
    id: "expert",
    name: "Expert",
    icon: <Crown className="w-6 h-6" />,
    color: "from-violet-500 to-purple-600",
    bgColor: "bg-violet-50",
    borderColor: "border-violet-400",
    textColor: "text-violet-700",
    description: "Section-precise, multi-step computations, transfer pricing, GAAR, restructuring",
    recommended: "CAs, Tax professionals & DISA holders",
    gp: "20 GP / question",
    gatedBy: "EXPERT_LOCKED",
    gateMessage: "Complete all Basic, Moderate & Advanced questions to unlock Expert level",
  },
  {
    id: "mixed",
    name: "Mixed",
    icon: <Shuffle className="w-6 h-6" />,
    color: "from-indigo-400 to-purple-500",
    bgColor: "bg-indigo-50",
    borderColor: "border-indigo-300",
    textColor: "text-indigo-700",
    description: "Balanced mix of all available difficulty levels",
    recommended: "Best overall practice",
    gp: "8–20 GP / question",
    gatedBy: null,
  },
];

const DifficultyModal = ({ isOpen, onClose, onSelect, difficultyStatus = {} }) => {
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  // Determine which levels are locked (exhausted = all questions done; gated = prerequisite not met)
  const isExhausted = (id) => {
    const s = difficultyStatus[id];
    if (!s) return false;
    return s.exhausted === true;
  };

  const isGated = (id) => {
    const s = difficultyStatus[id];
    if (!s) return false;
    return s.gated === true;
  };

  const isLocked = (id) => isExhausted(id) || isGated(id);

  const isMixedAllSeen = difficultyStatus?.mixed?.all_seen === true;
  const canPlayMixedToday = difficultyStatus?.mixed?.can_play_today !== false;

  // Auto-select first available level if current selection is locked
  const getDefaultSelected = () => {
    for (const d of difficulties) {
      if (!isLocked(d.id)) return d.id;
    }
    return null;
  };

  const effectiveSelected = selected !== null ? selected : getDefaultSelected();
  const allLocked = difficulties.every(d => isLocked(d.id));

  const handleStart = () => {
    if (!effectiveSelected || isLocked(effectiveSelected)) return;
    onSelect(effectiveSelected);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-2xl"
        >
          <Card className="bg-white rounded-3xl shadow-2xl border-0 overflow-hidden">
            <CardContent className="p-0">
              {/* Header */}
              <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-6 text-white">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: "Nunito" }}>
                      Choose Your Challenge Level
                    </h2>
                    <p className="text-emerald-100 text-sm">
                      {allLocked
                        ? "You've completed all levels today — come back tomorrow!"
                        : "Select a difficulty that matches your expertise"}
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-white/20 rounded-full transition-colors"
                    data-testid="close-difficulty-modal"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* All-seen banner */}
              {isMixedAllSeen && canPlayMixedToday && (
                <div className="mx-6 mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium flex items-center gap-2">
                  <span>🏆</span>
                  <span>You've seen all questions! You get <strong>1 review quiz today</strong>. Try challenging a friend for extra fun!</span>
                </div>
              )}

              {/* Difficulty Options */}
              <div className="p-6 space-y-3">
                {difficulties.map((diff) => {
                  const locked = isLocked(diff.id);
                  const status = difficultyStatus[diff.id];
                  const isSelected = effectiveSelected === diff.id && !locked;

                  return (
                    <button
                      key={diff.id}
                      onClick={() => { if (!locked) setSelected(diff.id); }}
                      data-testid={`difficulty-${diff.id}`}
                      disabled={locked}
                      className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${
                        locked
                          ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
                          : isSelected
                          ? `${diff.borderColor} ${diff.bgColor} shadow-md`
                          : "border-slate-200 hover:border-slate-300 cursor-pointer"
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className={`w-12 h-12 rounded-xl bg-gradient-to-br ${
                            locked ? "from-slate-300 to-slate-400" : diff.color
                          } text-white flex items-center justify-center`}
                        >
                          {locked ? <Lock className="w-6 h-6" /> : diff.icon}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className={`font-bold text-lg ${isSelected ? diff.textColor : locked ? "text-slate-400" : "text-slate-800"}`}>
                              {diff.name}
                            </h3>
                            {diff.id === "mixed" && !locked && (
                              <span className="text-xs bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full font-medium">
                                Recommended
                              </span>
                            )}
                            {isGated(diff.id) && (
                              <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                                <Lock className="w-3 h-3" />
                                Locked
                              </span>
                            )}
                            {isExhausted(diff.id) && (
                              <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                All done!
                              </span>
                            )}
                          </div>
                          <p className={`text-sm ${locked ? "text-slate-400" : "text-slate-600"}`}>
                            {isGated(diff.id)
                              ? diff.gateMessage || "Complete prerequisite levels to unlock"
                              : isExhausted(diff.id)
                              ? diff.id === "mixed"
                                ? "You've already played your daily review quiz. Come back tomorrow!"
                                : "You've answered all questions in this level. Try another level!"
                              : diff.description}
                          </p>
                          {!locked && (
                            <div className="flex items-center gap-3 mt-1">
                              <p className="text-xs text-slate-400">Best for: {diff.recommended}</p>
                              <span className={`text-xs font-bold ${diff.textColor} opacity-80`}>⚡ {diff.gp}</span>
                            </div>
                          )}
                          {!locked && status && status.remaining > 0 && (
                            <p className="text-xs text-emerald-600 mt-1 font-medium">
                              {status.remaining} question{status.remaining !== 1 ? "s" : ""} remaining
                            </p>
                          )}
                        </div>
                        {!locked && (
                          <div
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              isSelected ? `${diff.borderColor} ${diff.bgColor}` : "border-slate-300"
                            }`}
                          >
                            {isSelected && (
                              <div className={`w-3 h-3 rounded-full bg-gradient-to-br ${diff.color}`} />
                            )}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <div className="px-6 pb-6 space-y-3">
                {allLocked ? (
                  <Button
                    onClick={() => { onClose(); navigate("/challenges"); }}
                    className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white font-bold py-6 rounded-2xl text-lg shadow-lg"
                  >
                    Challenge a Friend Instead!
                  </Button>
                ) : (
                  <Button
                    onClick={handleStart}
                    disabled={!effectiveSelected || isLocked(effectiveSelected)}
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold py-6 rounded-2xl text-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="start-quiz-with-difficulty"
                  >
                    Start {difficulties.find((d) => d.id === effectiveSelected)?.name || ""} Quiz
                  </Button>
                )}
                {!allLocked && isMixedAllSeen && (
                  <button
                    onClick={() => { onClose(); navigate("/challenges"); }}
                    className="w-full text-center text-sm text-indigo-600 font-semibold hover:underline py-1"
                  >
                    Or challenge a friend for extra GP
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DifficultyModal;
